import Backbone from 'backbone';
import * as d3 from 'd3';
import $ from 'jquery';
import React from 'react';
import {debounce} from 'ts-debounce';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ALIGNMENT_SCROLLING_PERF_INTERVAL} from 'constants/AlignmentConstants';
import {Side} from 'constants/MolbioConstants';
import {getAminoAcidMatrix} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentStatsUtil';
import {getIdentityValues} from 'molbio/aa-sequences/alignments/AaSequenceAlignmentUtil';
import {PolymerAlignmentTabState} from 'molbio/maps/alignment/PolymerAlignmentTabView';
import AlignmentCommentsView from 'molbio/maps/linear/AlignmentCommentsView';
import Component from 'molbio/maps/linear/Component';
import ComponentWrapper from 'molbio/maps/linear/ComponentWrapper';
import LineView from 'molbio/maps/linear/LineView';
import ReactWrapperView from 'molbio/maps/linear/ReactWrapperView';
import SequenceAxisView from 'molbio/maps/linear/SequenceAxisView';
import Slice from 'molbio/maps/linear/Slice';
import TooltipBarGraph from 'molbio/maps/linear/TooltipBarGraph';
import {SummaryMapState, SummaryMapSingleState} from 'molbio/maps/MapStates';
import Range from 'molbio/maps/selectables/Range';
import AaSequenceAlignmentTabState from 'molbio/tabstates/AaSequenceAlignmentTabState';
import * as Perf from 'performance/Perf';
import {PerfId} from 'performance/Perf';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {MouseWheelEvent} from 'util/Mouse';
import movingAverage from 'util/movingAverage';

interface RangeDatum {
  start: number;
  end: number;
  type: 'range' | 'preview';
}

type RowComponentName = 'line';

type ComponentName = 'summaryIdentityGraph' | 'sequenceAxis' | 'alignmentComments';

export type AnyComponentName = RowComponentName | ComponentName;

export interface SummarySliceViewInitializationOptions<TRowState>
  // @ts-expect-error: Backbone expects a model
  extends Backbone.ViewOptions<SummaryMapState | null> {
  top?: boolean;
  marginBottom?: number;
  mapType: unknown;
  slice: Slice;
  tabstate: PolymerAlignmentTabState<TRowState>;
  y?: Array<AnyComponentName>;
}

/**
 * A component similar to SliceView that can render SliceView components for multiple sequences in an
 * alignment. If a component is named in `y`, that component will be rendered for each sequence associated
 * with the SummarySliceView. Two "global" components are also available: `sequenceAxis` and
 * `alignmentComments`. A tabstate may be either a (Sequence)AlignmentTabState or a AaSequenceAlignmentTabState,
 * so data should be interpreted through tabstate methods.
 */
// @ts-expect-error: Backbone expects a model
export default class SummarySliceView<TRowState> extends Backbone.View<SummaryMapState | null> {
  mode!: 'show';
  top!: boolean;
  marginBottom!: number;
  mapType!: unknown;
  tabstate!: PolymerAlignmentTabState<TRowState>;
  blinkCursor!: () => void;
  slice!: Slice;
  range!: Range;
  performanceId!: PerfId;
  y!: Array<AnyComponentName>;
  rowComponentMethods!: {
    [componentName in RowComponentName]: (mapstate: TRowState) => Component;
  };
  componentMethods!: {[componentName in ComponentName]: () => Component};
  listComponents!: Array<Component>;
  lineLayer!: d3.Selection<SVGElement, unknown, d3.BaseType, unknown>;
  sublineLayer!: d3.Selection<SVGElement, unknown, d3.BaseType, unknown>;

  // These seem to only be used by child classes.
  lazyStart?: number;
  lazyEnd?: number;

  static initClass(): void {
    this.prototype.tagName = 'svg';
    this.prototype.className = 'slice';
  }

  constructor(options: SummarySliceViewInitializationOptions<TRowState>) {
    super(options);
  }

  override events(): Backbone.EventsHash {
    return {
      mousedown: 'updateRange',
      mousemove: 'updateRange',
      mouseup: 'updateRange',
      dblclick: 'updateRange',
    };
  }

  triggerHeightChange(height: number): void {
    // TODO: Investigate if protein alignments need something like this.
    if (this.model instanceof SummaryMapState) {
      this.model.set('sliceHeight', height);
    }
  }

  override initialize(options: SummarySliceViewInitializationOptions<TRowState>): void {
    this.performanceId = Perf.start(
      'drawAlignmentSummarySlice',
      {enabled: false},
      {
        interval: ALIGNMENT_SCROLLING_PERF_INTERVAL,
        aggregations: [Perf.Aggregation.MEDIAN, Perf.Aggregation.MAX],
      },
      {}
    );
    this.mode = 'show';
    this.top = options.top || false;
    this.marginBottom = options.marginBottom || 0;
    this.mapType = options.mapType;
    this.tabstate = options.tabstate;
    this.blinkCursor = debounce(this.blinkCursor, 100);
    this.slice = options.slice;
    this.range = this.tabstate.get('range')!;
    this.y = options.y || ['line'];

    // Analogous to SliceView, we have methods keyed by component name that make it easy to render
    // a vertical list of these component by specifying an array of the component names.
    this.rowComponentMethods = {
      line: (mapstate) =>
        new LineView({
          // @ts-expect-error: Backbone expects model to always be a Backbone.Model instance
          model: mapstate,
          tabstate: this.tabstate,
          slice: this.slice,
          visibilityOptions: this.tabstate.getSummaryMapVisibilityOptions(),
          lineLayout: this.tabstate.getSummaryMapLineLayout(),
          parent: this,
        }),
    };
    this.componentMethods = {
      summaryIdentityGraph: () => {
        // This sliceview is only used for Protein alignments.
        const tabstate = this.tabstate as unknown as AaSequenceAlignmentTabState;
        // @ts-expect-error: Seemingly missing attributes (tabstate and name)
        return new ReactWrapperView({
          className: 'summaryIdentityGraph',
          setting: vo.SUMMARY_IDENTITY_GRAPH.name,
          model: this.tabstate,
          visibilityOptions: tabstate.get('visibilityOptions'),
          polymerLayoutName: 'summaryIdentityGraph',
          boxHeight: (layout) => layout.height + layout.vMargin,
          renderReact: (layout) => {
            const identityValues = getIdentityValues(
              getAminoAcidMatrix(tabstate.get('alignment')!.rows, this.slice.start(), this.slice.end())
            );
            const summaryIdentityValues = movingAverage(identityValues, layout.slidingWindowSize);
            return (
              <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
                <TooltipBarGraph
                  values={summaryIdentityValues}
                  maxValue={1}
                  xFn={(i) => this.slice.get('x')!(i + this.slice.start())}
                  maxHeight={layout.height}
                  baselineProps={{stroke: 'black'}}
                />
              </ComponentWrapper>
            );
          },
        });
      },
      sequenceAxis: () => {
        // This only applies to sequence alignments: safe to assume model is a (Sequence)AlignmentTabState
        const axisView = new SequenceAxisView({
          // @ts-expect-error
          model: this.tabstate.getSubmapstates()[0],
          tabstate: this.tabstate,
          slice: this.slice,
          parent: this,
        });
        axisView.shouldShow = () => true;
        return axisView;
      },
      alignmentComments: () => {
        // This only applies to sequence alignments: safe to assume model is a (Sequence)AlignmentTabState
        const commentsView = new AlignmentCommentsView({
          // @ts-expect-error
          model: this.model,
          tabstate: this.tabstate,
          slice: this.slice,
          parent: this,
          mapType: this.mapType,
        });
        commentsView.shouldShow = () => true;
        return commentsView;
      },
    };

    this.listComponents = []; // the list of components that are currently rendered
    this.setLineHeights();
  }

  registerGlobalMouseEvents(): void {
    this.unregisterGlobalMouseEvents();
    $(document).on(`mousemove.summary-${this.cid}`, (event) => {
      this.moveRangeUpdate(event);
    });
    $(document).on(`mouseup.summary-${this.cid}`, () => {
      this.finishRangeUpdate();
    });
  }

  unregisterGlobalMouseEvents(): void {
    $(document).off(`mousemove.summary-${this.cid}`);
    $(document).off(`mouseup.summary-${this.cid}`);
  }

  // TODO: Remove. height is purely a function of # of rows in alignment. If we do use a layout model,
  //       it could be at least be handled by the tabstate.
  setLineHeights(): void {
    const numSlices = this.tabstate.getSubmapstates().length;
    const height = Math.max(Math.round(100 / (numSlices + 2)), 10);
    const vMargin = Math.round((100 - height * numSlices) / numSlices);
    this.tabstate.setSummaryMapLineLayout({height, vMargin});
  }

  renderComponentsList(): void {
    // Remove any existing views:
    this.listComponents.forEach((component: Component): void => {
      this.stopListening(component);
      component.remove();
    });
    this.listComponents = [];

    // Iterate through this.y and instantiate the views.
    const masterCid = this.tabstate.getMasterSubmapstateCid();
    this.y.forEach((componentName) => {
      if (componentName in this.componentMethods) {
        const componentMethod = this.componentMethods[componentName as ComponentName];
        this.listComponents.push(componentMethod());
      } else if (componentName in this.rowComponentMethods) {
        this.tabstate.getSubmapstates().forEach((mapstate: TRowState) => {
          const mapStateCid = this.tabstate.getCidForSubmapstate(mapstate);
          // Only render components for top if this.top; render all but top if !this.top.
          if ((masterCid === mapStateCid) !== this.top) {
            return;
          }
          const rowComponentMethod = this.rowComponentMethods[componentName as RowComponentName];
          this.listComponents.push(rowComponentMethod(mapstate));
        });
      }
    });

    // Render each view and listen for redraws so we can re-layout the views.
    this.forEachVisibleView((view) => {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(view, 'redraw', this.adjustAndRange);
      const g = this.sublineLayer.append('svg:g').node();
      view.setElement(g);
      view.render(false);
    });
  }

  reorder(): void {
    this.renderComponentsList();
    this.adjustHeight();
  }

  rerenderForMapRemoval(): void {
    this.renderComponentsList();
    // @ts-expect-error: lazyStart may be undefined
    this.lazyStart = Math.max(this.lazyStart, 0);
    // @ts-expect-error: lazyEnd may be undefined
    this.lazyEnd = Math.min(this.lazyEnd, this.listComponents.length);
    this.adjustHeight();
  }

  registerListeners(): void {
    this.stopListening();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.tabstate, 'change:polymerLayout', this.update);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.slice, 'change', this.update);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.range, 'change', this.doRange);

    // For sequence alignments, deleting rows and re-ordering require manual re-render.
    if (this.model instanceof SummaryMapState) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      this.listenTo(this.model.get('submapstates'), 'sort', this.reorder);
      this.listenTo(this.model.get('submapstates'), 'remove', (mapstate: SummaryMapSingleState): void => {
        this.rerenderForMapRemoval();
      });
    }
  }

  createRangeRect(
    root: d3.Selection<d3.BaseType, RangeDatum, SVGElement, unknown>,
    x: (value: number) => number,
    visible: boolean = true
  ): void {
    let height = this.boxHeight();
    height = Math.max(height, 30);
    root
      .attr('x', (d) => x(d.start))
      .attr('y', 0)
      .attr('width', (d) => x(d.end) - x(d.start))
      .attr('height', (d) => height)
      .attr('fill-opacity', !visible ? 0 : 0.5)
      .classed('range-preview', (d) => d.type === 'preview');

    root.exit().remove();
  }

  renderRange(): void {
    const data: Array<RangeDatum> = [];

    data.push({
      start: this.range.start(),
      end: this.range.end(),
      type: 'range',
    });

    if (this.range.get('active')) {
      const rangePreview = this.range.preview().correct();
      data.push({
        start: rangePreview.start(),
        end: rangePreview.end(),
        type: 'preview',
      });
    }

    let visible = this.lineLayer.selectAll('rect.range').data(data);
    const hidden = this.lineLayer.selectAll('rect.range-hidden').data(data);

    const visibleEnter = visible.enter().insert('svg:rect', '.alignmentComments').classed('range', true);
    visible = visible.merge(visibleEnter);

    const {range, slice} = this;

    const drag = d3
      .drag()
      .subject((d) => ({
        x: slice.x()(range.start()), // Don't store slice.x to avoid closuring an old value of slice.x
        y: 0,
      }))
      // @ts-expect-error: types only expect two args, possibly out-of-date
      .on('drag', (e, d, i) => {
        const xInv = slice.get('xInv')!;
        range.panStart(Math.floor(xInv(e.x)));
      })
      .on('start', (e: d3.D3DragEvent<Element, unknown, unknown>) => {
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', true);
        e.sourceEvent.stopPropagation();
      })
      .on('end', () => {
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', false);
      });

    hidden
      .enter()
      .insert('svg:rect', '.alignmentComments')
      .classed('range-hidden', true)
      .call(drag)
      .on('mousedown', (e) => {
        // Prevent updateRange from also handling the mousedown event.
        e.stopPropagation();
      });

    const xFn = this.slice.x();
    this.createRangeRect(visible, xFn, true);
    this.createRangeRect(hidden, xFn, false);

    visible.exit().remove();
    hidden.exit().remove();
  }

  die(): void {
    this.stopListening();
    $(document).off('.summary');
    // eslint-disable-next-line no-restricted-syntax
    _.each(this.listComponents, (view, className, list) => {
      view.die();
    });
  }

  getTop(): number {
    return this.el.offsetTop - 10;
  }

  finishRangeUpdate(): void {
    this.lineLayer.selectAll('.range-hidden').classed('grabbed', false);
    if (this.range.get('active')) {
      this.range.correct();
      analytics.track('alignment', 'resized_summary_view_range', {
        start: this.range.start(),
        end: this.range.end(),
        span: this.range.span(),
      });
    }
    this.range.set('active', false);
    this.range.set('dragging', false);
  }

  moveRangeUpdate(e: JQueryEventObject): void {
    const offset = e.pageX - this.$el.offset()!.left;
    const x = Math.round(this.slice.xInv()(offset));
    if (this.range.get('active')) {
      this.range.extend(x);
    }
    if (this.range.get('dragging')) {
      this.range.pan(x);
    }
  }

  updateRange(event: JQueryEventObject): void {
    event.preventDefault();
    const offset = event.pageX - this.$el.offset()!.left;
    const x = Math.round(this.slice.xInv()(offset));
    switch (event.type) {
      case 'mousemove':
        this.moveRangeUpdate(event);
        break;
      case 'mousedown':
        this.lineLayer.selectAll('.range-hidden').classed('grabbed', true);
        this.range.pan(x);
        this.range.set('dragging', true);
        this.registerGlobalMouseEvents();
        event.stopPropagation();
        break;
      case 'mouseup':
        if (this.range.get('dragging') || this.range.get('active')) {
          this.finishRangeUpdate();
          this.unregisterGlobalMouseEvents();
        }
        break;
      case 'dblclick':
        // range.set(start: 0, end: @sequence.length())
        event.stopPropagation();
        break;
      default:
        break;
    }
  }

  doRange(): void {
    Perf.markStart(this.performanceId);

    this.renderRange();
    this.rangeLines();

    Perf.markEnd(this.performanceId);
  }

  rangeLines(): void {
    interface RangeLineDatum {
      x: number;
      type: Side;
    }
    const x = this.slice.x();
    const data: Array<RangeLineDatum> = [];
    data.push({
      x: this.range.start(),
      type: 'start',
    });

    data.push({
      x: this.range.end(),
      type: 'end',
    });

    const hide = false;
    const {range} = this;
    const boxHeight = this.boxHeight();

    this.lineLayer
      .selectAll('.range-line')
      .data(data)
      .join('svg:line')
      .classed('range-line', true)
      .attr('x1', (d: RangeLineDatum): number => x(d.x))
      .attr('y1', 0)
      .attr('x2', (d: RangeLineDatum): number => x(d.x))
      .attr('y2', boxHeight)
      .attr('style', 'cursor:ew-resize')
      .attr('stroke', 'black')
      .attr('stroke-width', 2)
      .classed('hide', hide)
      .on('mousedown', (e, d: RangeLineDatum): void => {
        e.stopPropagation();
        range.set({active: true, freeEnd: d.type});
      });
  }

  boxHeight(index?: number): number {
    let extra = 0;
    if (index == null) {
      index = this.listComponents.length;
      extra = this.marginBottom;
    }
    return this.listComponents
      .slice(0, index)
      .reduce((total, component) => total + component.boxHeight(), extra);
  }

  adjustHeight(): void {
    this.forEachVisibleView((component, index) => {
      d3.select(component.el).attr('transform', `translate(0,${this.boxHeight(index)})`);
    });
    const boxHeight = this.boxHeight();
    d3.select(this.el).attr('height', boxHeight);
    this.lineLayer.select('rect.action-rect').attr('height', boxHeight);
    this.triggerHeightChange(boxHeight);
  }

  update(): void {
    this.forEachVisibleView((view) => {
      view.update(false);
    });
    this.adjustAndRange();
  }

  adjustAndRange(): void {
    this.adjustHeight();
    this.doRange();
  }

  setWidth(width: number): void {
    d3.select(this.el).attr('width', width);
    if (this.lineLayer) {
      this.lineLayer.select('rect.action-rect').attr('width', width);
    }
  }

  forEachVisibleView(fn: (view: Component, i: number) => void): void {
    for (let i = 0; i < this.listComponents.length; i++) {
      const view = this.listComponents[i];
      fn(view, i);
    }
  }

  override render(): this {
    this.registerListeners();

    // sublineLayer is appended before lineLayer so it has a lower z-index. sublineLayer is where all of the
    // row views are rendered, while lineLayer is where the selection / range views are rendered, so that
    // they are rendered above the row views. NB: lineLayer is a bit of a misnomer, since the LineViews
    // are actually rendered into the sublineLayer.
    this.sublineLayer = d3.select(this.el).append('svg:g');
    this.lineLayer = d3.select(this.el).append('svg:g');
    this.lineLayer
      .append('svg:rect')
      .classed('action-rect', true)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', d3.select(this.el).attr('width'))
      .attr('height', 0)
      .attr('fill', 'blue')
      .attr('fill-opacity', 0);

    this.renderComponentsList();
    this.adjustAndRange();

    $(this.el).on(`${MouseWheelEvent}.summary`, (e: JQueryEventObject): void => {
      let rx;
      // @ts-expect-error: missing types for WheelEvent details
      if (e.originalEvent.detailX != null) {
        // @ts-expect-error: missing types for WheelEvent details
        rx = e.originalEvent.detailX;
        // @ts-expect-error: missing types for WheelEvent details
      } else if (e.originalEvent.wheelDeltaX != null) {
        // @ts-expect-error: missing types for WheelEvent details
        rx = e.originalEvent.wheelDeltaX / 16;
        // @ts-expect-error: missing types for WheelEvent details
      } else if (e.originalEvent.detail != null) {
        // @ts-expect-error: missing types for WheelEvent details
        rx = -e.originalEvent.detail;
      } else {
        rx = 0;
      }
      let ry;
      // @ts-expect-error: missing types for WheelEvent details
      if (e.originalEvent.detailY != null) {
        // @ts-expect-error: missing types for WheelEvent details
        ry = e.originalEvent.detailY;
        // @ts-expect-error: missing types for WheelEvent details
      } else if (e.originalEvent.wheelDeltaY != null) {
        // @ts-expect-error: missing types for WheelEvent details
        ry = e.originalEvent.wheelDeltaY / 16;
        // @ts-expect-error: missing types for WheelEvent details
      } else if (e.originalEvent.detail != null) {
        // @ts-expect-error: missing types for WheelEvent details
        ry = -e.originalEvent.detail;
      } else {
        ry = 0;
      }
      if (Math.abs(rx) > Math.abs(ry)) {
        this.range.panStart(this.range.start() - rx);
        e.preventDefault();
      }
    });
    return this;
  }
}
SummarySliceView.initClass();
